import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { seoVariables } from '@constants';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { convertTime } from '@utils';
import { GlobalsService } from '../globals/globals.service';

@Injectable({
    providedIn: 'root',
})
export class SeoService {
    days = {
        mon: 'Monday',
        tue: 'Tuesday',
        wed: 'Wednesday',
        thu: 'Thursday',
        fri: 'Friday',
        sat: 'Saturday',
        sun: 'Sunday',
    };

    daysShort = {
        mon: 'Mo',
        tue: 'Tu',
        wed: 'We',
        thu: 'Th',
        fri: 'Fr',
        sat: 'Sa',
        sun: 'Su',
    };

    constructor(
        @Inject(DOCUMENT) private doc,
        private translateService: TranslateService,
        private meta: Meta,
        private title: Title,
    ) {}

    getOutletSchema(outlet) {
        return {
            '@context': 'https://schema.org',
            '@type': 'CafeOrCoffeeShop',
            name: outlet?.name,
            image: outlet?.profile_image_url.length
                ? outlet.profile_image_url
                : `${environment.outletPortalUrl}/assets/images/empty.svg`,
            '@id': outlet.id.toString(),
            url: outlet?.website,
            telephone: outlet?.phone,
            address: {
                '@type': 'PostalAddress',
                streetAddress: outlet?.address1,
                addressLocality: outlet?.address2,
                postalCode: outlet?.zip,
                addressCountry: outlet?.country,
            },
            geo: {
                '@type': 'GeoCoordinates',
                latitude: outlet?.latitude,
                longitude: outlet?.longitude,
            },
            openingHours: Object.keys(outlet?.timing)
                .filter((day) => outlet?.timing[day])
                .map((day) => {
                    return `${this.daysShort[day]} ${convertTime(outlet.timing[day].open_time)}-${convertTime(
                        outlet.timing[day].close_time,
                    )}`;
                }),
        };
    }

    getBreadcrumbSchema(city, slug = null, outletName = null) {
        const itemList = [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Era of we Outlet Portals',
                item: `https://eraofwe.com/op/${this.translateService.instant('coffee')}`,
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: city,
                item: `https://eraofwe.com/op/${this.translateService.instant('coffee')}/${city}`,
            },
        ];
        if (slug && outletName) {
            itemList.push({
                '@type': 'ListItem',
                position: 3,
                name: outletName,
                item: `https://eraofwe.com/op/${this.translateService.instant('coffee')}/${city}/${slug}`,
            });
        }
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            name: outletName || city,
            itemListElement: itemList,
        };
    }

    setPageTitle(title: string) {
        this.title.setTitle(title?.substr(0, 60));
    }

    getPageTitle() {
        return this.title.getTitle();
    }

    setMetaData(type, name, content) {
        const metaData: any = {
            [type]: name,
            content: name === 'description' ? content?.substr(0, 160) : content,
        };
        this.meta.updateTag(metaData);
    }

    createLinkForCanonicalURL() {
        if (this.doc.URL?.includes('https')) {
            const existingLink = this.doc.querySelector('link[rel="canonical"]');
            if (existingLink) {
                existingLink.setAttribute('href', this.doc.URL);
            } else {
                const newLink: HTMLLinkElement = this.doc.createElement('link');
                newLink.setAttribute('rel', 'canonical');
                this.doc.head.appendChild(newLink);
                newLink.setAttribute('href', this.doc.URL);
            }
        }
    }

    setSEO(title: string, description: string) {
        this.setPageTitle(title);
        this.setMetaData('name', 'description', description);

        this.setMetaData('property', 'og:title', title);
        this.setMetaData('property', 'og:description', description);
        this.setMetaData('property', 'og:url', this.doc.URL);
        this.setMetaData('property', 'og:image', `${seoVariables.image}?v=${Date.now()}`);

        this.setMetaData('name', 'twitter:image', seoVariables.image);
        this.setMetaData('name', 'twitter:creator', seoVariables.author);
        this.setMetaData('name', 'twitter:site', this.doc.URL);
        this.setMetaData('name', 'twitter:title', title);
        this.setMetaData('name', 'twitter:description', description);

        this.createLinkForCanonicalURL();
    }
}
